import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Checkbox, FormControlLabel, FormGroup,
  FormControl, FormLabel, Typography, Box, Divider,
  CircularProgress, Chip, TextField, Alert
} from '@mui/material';
import { annualReviewApi } from '../services/api';

const AnnualSecurityReviewDialog = ({ open, onClose, clientId, clientName }) => {
  const [step, setStep] = useState('select'); // 'select' | 'summary'
  const [loading, setLoading] = useState(true);
  const [clientData, setClientData] = useState(null);
  const [error, setError] = useState('');

  // Selected sections
  const [selectedAssessments, setSelectedAssessments] = useState({});
  const [selectedVulnScan, setSelectedVulnScan] = useState(false);
  const [selectedPentest, setSelectedPentest]     = useState(false);
  const [selectedAdAudit, setSelectedAdAudit]     = useState(false);
  const [selectedVor, setSelectedVor]             = useState(false);
  const [selectedNar, setSelectedNar]             = useState(false);
  const [selectedDarkWeb, setSelectedDarkWeb]     = useState(false);
  const [selectedAiGov, setSelectedAiGov]         = useState(false);

  // CISO summary
  const [analyzing, setAnalyzing]           = useState(false);
  const [cisoSummary, setCisoSummary]       = useState('');
  const [summaryEdited, setSummaryEdited]   = useState(false);
  const [editingSummary, setEditingSummary] = useState(false);
  const [savingSummary, setSavingSummary]   = useState(false);

  // PDF generation
  const [generatingPdf, setGeneratingPdf]   = useState(false);

  useEffect(() => {
    if (open && clientId) {
      loadData();
    }
  }, [open, clientId]);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await annualReviewApi.getClientData(clientId);
      const { data, saved_review } = res.data;
      setClientData(data);

      // Pre-select available items
      const assessmentMap = {};
      (data.assessments || []).forEach(a => { assessmentMap[a.type_name] = true; });
      setSelectedAssessments(assessmentMap);
      setSelectedVulnScan(!!data.vuln_scan);
      setSelectedPentest(!!data.pentest);
      setSelectedAdAudit(!!data.ad_audit);
      setSelectedVor(!!data.vor);
      setSelectedNar(!!data.nar);
      setSelectedDarkWeb(!!data.dark_web);
      setSelectedAiGov(!!data.ai_governance);

      // Restore saved summary if present
      if (saved_review?.ciso_summary) {
        setCisoSummary(saved_review.ciso_summary);
        setSummaryEdited(!!saved_review.ciso_summary_edited);
        if (saved_review.selected_sections) {
          const ss = saved_review.selected_sections;
          if (ss.assessments) {
            const m = {};
            ss.assessments.forEach(t => { m[t] = true; });
            setSelectedAssessments(m);
          }
          setSelectedVulnScan(!!ss.vuln_scan);
          setSelectedPentest(!!ss.pentest);
          setSelectedAdAudit(!!ss.ad_audit);
          setSelectedVor(!!ss.vor);
          setSelectedNar(!!ss.nar);
          setSelectedDarkWeb(!!ss.dark_web);
          setSelectedAiGov(!!ss.ai_governance);
        }
        setStep('summary');
      }
    } catch (err) {
      setError('Failed to load client data.');
    } finally {
      setLoading(false);
    }
  };

  const buildSelectedSections = () => ({
    assessments: Object.entries(selectedAssessments).filter(([, v]) => v).map(([k]) => k),
    vuln_scan:    selectedVulnScan,
    pentest:      selectedPentest,
    ad_audit:     selectedAdAudit,
    vor:          selectedVor,
    nar:          selectedNar,
    dark_web:     selectedDarkWeb,
    ai_governance: selectedAiGov,
  });

  const hasSelections = () => {
    const ss = buildSelectedSections();
    return ss.assessments.length > 0 || ss.vuln_scan || ss.pentest ||
           ss.ad_audit || ss.vor || ss.nar || ss.dark_web || ss.ai_governance;
  };

  const handleAnalyze = async () => {
    if (cisoSummary) {
      const msg = summaryEdited
        ? 'You have manually edited this summary. Analyzing again will overwrite your changes. Continue?'
        : 'This will replace the existing AI-generated summary. Continue?';
      if (!window.confirm(msg)) return;
    }
    setAnalyzing(true);
    setError('');
    try {
      const res = await annualReviewApi.analyze(clientId, buildSelectedSections());
      setCisoSummary(res.data.ciso_summary);
      setSummaryEdited(false);
      setEditingSummary(false);
      setStep('summary');
    } catch (err) {
      setError('AI analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveSummary = async () => {
    setSavingSummary(true);
    try {
      await annualReviewApi.saveSummary(clientId, cisoSummary, buildSelectedSections());
      setSummaryEdited(true);
      setEditingSummary(false);
    } catch (err) {
      setError('Failed to save summary.');
    } finally {
      setSavingSummary(false);
    }
  };

  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    try {
      const res = await annualReviewApi.generatePdf(clientId, buildSelectedSections(), cisoSummary);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (clientName || 'Client').replace(/\s+/g, '_');
      const mo = new Date().toISOString().slice(0, 7).replace('-', '');
      link.setAttribute('download', `${safeName}_Annual_Security_Review_${mo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      handleClose();
    } catch (err) {
      setError('Failed to generate PDF.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleClose = () => {
    setStep('select');
    setError('');
    setEditingSummary(false);
    onClose();
  };

  const toggleAssessment = (typeName) => {
    setSelectedAssessments(prev => ({ ...prev, [typeName]: !prev[typeName] }));
  };

  const renderSection = (label, available, checked, onChange, detail) => (
    <FormControlLabel
      control={
        <Checkbox
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={!available}
          color="primary"
        />
      }
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: available ? 'text.primary' : 'text.disabled' }}>
            {label}
          </Typography>
          {available && detail && (
            <Typography variant="caption" color="text.secondary">({detail})</Typography>
          )}
          {!available && (
            <Chip label="Not Completed" size="small" sx={{ fontSize: '10px', height: '18px' }} />
          )}
        </Box>
      }
      sx={{ mb: 0.5 }}
    />
  );

  const d = clientData;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ color: '#6B3FA0', fontWeight: 'bold' }}>
          Annual Security Review
        </Typography>
        {clientName && (
          <Typography variant="body2" color="text.secondary">{clientName}</Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#6B3FA0' }} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && d && (
          <>
            {/* ── Step tabs ── */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={step === 'select' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setStep('select')}
                sx={{ bgcolor: step === 'select' ? '#6B3FA0' : 'transparent',
                      borderColor: '#6B3FA0', color: step === 'select' ? 'white' : '#6B3FA0' }}
              >
                1. Select Sections
              </Button>
              <Button
                variant={step === 'summary' ? 'contained' : 'outlined'}
                size="small"
                onClick={() => cisoSummary && setStep('summary')}
                disabled={!cisoSummary}
                sx={{ bgcolor: step === 'summary' ? '#6B3FA0' : 'transparent',
                      borderColor: '#6B3FA0', color: step === 'summary' ? 'white' : '#6B3FA0' }}
              >
                2. CISO Summary
              </Button>
            </Box>

            {/* ── Section Selection ── */}
            {step === 'select' && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the sections to include in the Annual Security Review.
                  Items not yet completed for this client are shown grayed out.
                </Typography>

                {/* Assessments */}
                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                  <FormLabel component="legend" sx={{ color: '#6B3FA0', fontWeight: 'bold', mb: 1 }}>
                    Compliance Assessments
                  </FormLabel>
                  <FormGroup>
                    {(d.assessments && d.assessments.length > 0) ? (
                      d.assessments.map(a => (
                        <FormControlLabel
                          key={a.type_name}
                          control={
                            <Checkbox
                              checked={!!selectedAssessments[a.type_name]}
                              onChange={() => toggleAssessment(a.type_name)}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2">{a.type_name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                ({a.date} · {a.score !== null ? `${a.score}% · ${a.grade}` : 'Score pending'})
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 0.5 }}
                        />
                      ))
                    ) : (
                      <FormControlLabel
                        control={<Checkbox disabled />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" color="text.disabled">No assessments</Typography>
                            <Chip label="Not Completed" size="small" sx={{ fontSize: '10px', height: '18px' }} />
                          </Box>
                        }
                      />
                    )}
                  </FormGroup>
                </FormControl>

                <Divider sx={{ my: 1.5 }} />

                <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                  <FormLabel component="legend" sx={{ color: '#6B3FA0', fontWeight: 'bold', mb: 1 }}>
                    Security Testing & Monitoring
                  </FormLabel>
                  <FormGroup>
                    {renderSection('Network Assessment Report (NAR)', !!d.nar, selectedNar, setSelectedNar,
                      d.nar ? `${d.nar.date} · ${d.nar.score}% · ${d.nar.grade}` : null)}
                    {renderSection('Vulnerability Scan', !!d.vuln_scan, selectedVulnScan, setSelectedVulnScan,
                      d.vuln_scan ? `${d.vuln_scan.date} · ${d.vuln_scan.total} findings` : null)}
                    {renderSection('Penetration Test', !!d.pentest, selectedPentest, setSelectedPentest,
                      d.pentest ? `${d.pentest.date} · ${d.pentest.total} findings` : null)}
                    {renderSection('Active Directory Audit', !!d.ad_audit, selectedAdAudit, setSelectedAdAudit,
                      d.ad_audit ? `${d.ad_audit.date} · ${d.ad_audit.total} findings` : null)}
                    {renderSection('Dark Web Monitoring', !!d.dark_web, selectedDarkWeb, setSelectedDarkWeb,
                      d.dark_web ? `${d.dark_web.total_exposures} exposures across ${d.dark_web.unique_breaches} breaches` : null)}
                  </FormGroup>
                </FormControl>

                <Divider sx={{ my: 1.5 }} />

                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend" sx={{ color: '#6B3FA0', fontWeight: 'bold', mb: 1 }}>
                    Risk & Governance
                  </FormLabel>
                  <FormGroup>
                    {renderSection('Value of Risk', !!d.vor, selectedVor, setSelectedVor,
                      d.vor ? `${d.vor.date} · Tolerance: ${d.vor.risk_tolerance_level || 'N/A'}` : null)}
                    {renderSection('AI Governance', !!d.ai_governance, selectedAiGov, setSelectedAiGov,
                      d.ai_governance ? `${d.ai_governance.tier} · ${d.ai_governance.approved_tools} approved tools` : null)}
                  </FormGroup>
                </FormControl>

                {!hasSelections() && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    Please select at least one section to include.
                  </Alert>
                )}
              </Box>
            )}

            {/* ── CISO Summary ── */}
            {step === 'summary' && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#6B3FA0' }}>
                    CISO Executive Summary
                    {summaryEdited && (
                      <Chip label="Edited" size="small" color="warning"
                        sx={{ ml: 1, fontSize: '10px', height: '18px' }} />
                    )}
                  </Typography>
                  {!editingSummary ? (
                    <Button size="small" variant="outlined"
                      sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
                      onClick={() => setEditingSummary(true)}>
                      Edit
                    </Button>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" onClick={() => setEditingSummary(false)}>Cancel</Button>
                      <Button size="small" variant="contained"
                        sx={{ bgcolor: '#6B3FA0' }}
                        onClick={handleSaveSummary}
                        disabled={savingSummary}>
                        {savingSummary ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  )}
                </Box>

                {editingSummary ? (
                  <TextField
                    multiline rows={18} fullWidth
                    value={cisoSummary}
                    onChange={(e) => setCisoSummary(e.target.value)}
                    sx={{ fontFamily: 'monospace' }}
                    inputProps={{ style: { fontSize: '13px', lineHeight: 1.6 } }}
                  />
                ) : (
                  <Box sx={{
                    p: 2, border: '1px solid #e0e0e0', borderRadius: 1,
                    borderLeft: '4px solid #6B3FA0',
                    backgroundColor: '#fafafa',
                    fontSize: '14px', lineHeight: 1.7,
                    maxHeight: '380px', overflowY: 'auto'
                  }}>
                    {cisoSummary.split('\n\n').map((block, i) => {
                      const trimmed = block.trim();
                      if (!trimmed) return null;
                      const lines = trimmed.split('\n');
                      const firstLine = lines[0];
                      const isHeading = firstLine.length < 60 && !firstLine.startsWith(' ') && !firstLine.endsWith('.');
                      if (isHeading && lines.length >= 2) {
                        return (
                          <Box key={i} sx={{ mb: 1.5 }}>
                            <Typography sx={{ fontWeight: 'bold', color: '#6B3FA0', fontSize: '14px', mb: 0.5 }}>
                              {firstLine}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', lineHeight: 1.7 }}>
                              {lines.slice(1).join('\n')}
                            </Typography>
                          </Box>
                        );
                      }
                      if (isHeading && lines.length === 1) {
                        return (
                          <Typography key={i} sx={{ fontWeight: 'bold', color: '#6B3FA0', fontSize: '14px', mb: 0.5, mt: i > 0 ? 1.5 : 0 }}>
                            {firstLine}
                          </Typography>
                        );
                      }
                      return (
                        <Typography key={i} sx={{ fontSize: '14px', lineHeight: 1.7, mb: 1.5, whiteSpace: 'pre-wrap' }}>
                          {trimmed}
                        </Typography>
                      );
                    })}
                  </Box>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  This summary will appear as the Executive Summary page in the PDF report.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={handleClose} color="inherit">Cancel</Button>
        <Box sx={{ flex: 1 }} />

        {step === 'select' && (
          <Button
            variant="outlined"
            onClick={handleAnalyze}
            disabled={analyzing || !hasSelections() || loading}
            sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
            startIcon={analyzing ? <CircularProgress size={16} /> : null}
          >
            {analyzing ? 'Analyzing...' : '🤖 Analyze with AI'}
          </Button>
        )}

        {step === 'summary' && (
          <>
            <Button
              variant="outlined"
              onClick={handleAnalyze}
              disabled={analyzing}
              sx={{ borderColor: '#6B3FA0', color: '#6B3FA0' }}
            >
              {analyzing ? 'Analyzing...' : '🔄 Re-analyze'}
            </Button>
          </>
        )}

        <Button
          variant="contained"
          onClick={handleGeneratePdf}
          disabled={generatingPdf || !hasSelections() || loading}
          sx={{ bgcolor: '#6B3FA0', '&:hover': { bgcolor: '#4A2B70' } }}
          startIcon={generatingPdf ? <CircularProgress size={16} sx={{ color: 'white' }} /> : null}
        >
          {generatingPdf ? 'Generating...' : '⬇ Generate PDF Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnualSecurityReviewDialog;
